/**
 * OS dialog store — replaces window.alert / confirm / prompt.
 * Glass modal, same chrome language as OS context menus.
 */
import { useEffect, useState } from "react";

export type DialogKind = "alert" | "confirm" | "prompt" | "dateRange";

export type DateRangeValue = { from: number; to: number };

export type DialogRequest = {
  id: string;
  kind: DialogKind;
  title: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  defaultValue?: string;
  placeholder?: string;
  danger?: boolean;
  /** dateRange defaults, epoch seconds. */
  defaultFrom?: number;
  defaultTo?: number;
  resolve: (value: boolean | string | DateRangeValue | null) => void;
};

type Listener = () => void;

class DialogStore {
  queue: DialogRequest[] = [];
  private listeners = new Set<Listener>();

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private emit() {
    for (const fn of this.listeners) fn();
  }

  get current(): DialogRequest | null {
    return this.queue[0] ?? null;
  }

  private enqueue(
    req: Omit<DialogRequest, "id">
  ) {
    const id = `dlg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.queue.push({ ...req, id });
    this.emit();
  }

  alert(opts: {
    title?: string;
    message: string;
    okLabel?: string;
  }): Promise<void> {
    return new Promise((resolve) => {
      this.enqueue({
        kind: "alert",
        title: opts.title || "ArcLog OS",
        message: opts.message,
        okLabel: opts.okLabel || "OK",
        resolve: () => resolve(),
      });
    });
  }

  confirm(opts: {
    title?: string;
    message: string;
    okLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
  }): Promise<boolean> {
    return new Promise((resolve) => {
      this.enqueue({
        kind: "confirm",
        title: opts.title || "Confirm",
        message: opts.message,
        okLabel: opts.okLabel || "OK",
        cancelLabel: opts.cancelLabel || "Cancel",
        danger: opts.danger,
        resolve: (v) => resolve(Boolean(v)),
      });
    });
  }

  prompt(opts: {
    title?: string;
    message?: string;
    defaultValue?: string;
    placeholder?: string;
    okLabel?: string;
    cancelLabel?: string;
  }): Promise<string | null> {
    return new Promise((resolve) => {
      this.enqueue({
        kind: "prompt",
        title: opts.title || "Input",
        message: opts.message || "",
        defaultValue: opts.defaultValue ?? "",
        placeholder: opts.placeholder,
        okLabel: opts.okLabel || "OK",
        cancelLabel: opts.cancelLabel || "Cancel",
        resolve: (v) => resolve(typeof v === "string" ? v : null),
      });
    });
  }

  dateRange(opts?: {
    title?: string;
    message?: string;
    defaultFrom?: number;
    defaultTo?: number;
    okLabel?: string;
    cancelLabel?: string;
  }): Promise<DateRangeValue | null> {
    const nowSec = Math.floor(Date.now() / 1000);
    const defaultTo =
      opts?.defaultTo !== undefined && Number.isFinite(opts.defaultTo)
        ? opts.defaultTo
        : nowSec;
    const defaultFrom =
      opts?.defaultFrom !== undefined && Number.isFinite(opts.defaultFrom)
        ? opts.defaultFrom
        : defaultTo - 90 * 86400;
    return new Promise((resolve) => {
      this.enqueue({
        kind: "dateRange",
        title: opts?.title || "Pick a date range",
        message: opts?.message || "",
        defaultFrom,
        defaultTo,
        okLabel: opts?.okLabel || "Apply",
        cancelLabel: opts?.cancelLabel || "Cancel",
        resolve: (v) =>
          resolve(
            v && typeof v === "object" && "from" in v && "to" in v
              ? (v as DateRangeValue)
              : null
          ),
      });
    });
  }

  settle(value: boolean | string | DateRangeValue | null) {
    const cur = this.queue.shift();
    if (!cur) return;
    try {
      cur.resolve(value);
    } finally {
      this.emit();
    }
  }

  dismiss() {
    const cur = this.current;
    if (!cur) return;
    if (cur.kind === "alert") this.settle(true);
    else if (cur.kind === "confirm") this.settle(false);
    else this.settle(null);
  }
}

export const dialogStore = new DialogStore();

export function useDialog(): DialogRequest | null {
  const [cur, setCur] = useState(dialogStore.current);
  useEffect(
    () => dialogStore.subscribe(() => setCur(dialogStore.current)),
    []
  );
  return cur;
}
